import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { certificateDays } from '@his/shared';
import { FileBadge, FileText } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { todayIso } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, FormSkeleton, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { safeReturnTo } from './BookAppointmentPage';
import { CERTIFICATE_TYPE_LABELS, certificateBody, printCertificate, type CertificateType } from './CertificatesPanel';

interface EncounterSummary {
  id: string;
  patient: { id: string; fullName: string };
}

export function IssueCertificatePage() {
  const { encounterId = '' } = useParams<{ encounterId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), `/consultations/${encounterId}?tab=certificates`);

  const encounter = useQuery({
    queryKey: ['encounter', encounterId],
    queryFn: () => get<EncounterSummary>(`/encounters/${encounterId}`),
    enabled: Boolean(encounterId),
  });

  if (!encounter.data) {
    return (
      <FormScreen title={tr('opd', 'issue_a_certificate')} icon={FileBadge} backTo={returnTo}>
        {encounter.isLoading ? (
          <FormSkeleton />
        ) : (
          <Alert variant="destructive">{describeError(encounter.error)}</Alert>
        )}
      </FormScreen>
    );
  }

  return (
    <IssueCertificateForm
      key={encounter.data.id}
      encounterId={encounter.data.id}
      patientId={encounter.data.patient.id}
      patientName={encounter.data.patient.fullName}
      returnTo={returnTo}
    />
  );
}

function IssueCertificateForm({
  encounterId,
  patientId,
  patientName,
  returnTo,
}: {
  encounterId: string;
  patientId: string;
  patientName: string;
  returnTo: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [type, setType] = useState<CertificateType>('sick_leave');
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [remarks, setRemarks] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [body, setBody] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const withPeriod = type === 'sick_leave' || type === 'fitness';
  const text = body ?? certificateBody(type, patientName, withPeriod ? from : '', type === 'sick_leave' ? to : '', remarks);
  const days = type === 'sick_leave' ? certificateDays(from, to) : null;

  const issue = useMutation({
    mutationFn: () =>
      post<{ id: string }>('/certificates', {
        patientId,
        encounterId,
        type,
        ...(withPeriod && from ? { fromDate: `${from}T00:00:00.000Z` } : {}),
        ...(type === 'sick_leave' && to ? { toDate: `${to}T00:00:00.000Z` } : {}),
        ...(days ? { days } : {}),
        ...(externalRef.trim() ? { externalRef: externalRef.trim() } : {}),
        body: text,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['encounter-certificates', encounterId] });
      void printCertificate(created.id);
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty =
    !issue.isSuccess &&
    (type !== 'sick_leave' || from !== todayIso() || to !== todayIso() || remarks !== '' || externalRef !== '' || body !== null);

  return (
    <FormScreen
      title={tr('opd', 'issue_a_certificate')}
      description={patientName}
      icon={FileBadge}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (text.trim().length < 10) return;
        setError(null);
        issue.mutate();
      }}
    >
      <FormSection title={tr('opd', 'details')} icon={FileBadge} columns={3}>
        <Field label={tr('opd', 'type')}>
          <ChoiceButtons
            layout="chips"
            value={type}
            onChange={(value) => {
              setType(value);
              setBody(null);
            }}
            options={(Object.keys(CERTIFICATE_TYPE_LABELS) as CertificateType[]).map((value) => ({
              value,
              label: CERTIFICATE_TYPE_LABELS[value],
            }))}
          />
        </Field>
        {withPeriod && (
          <Field label={tr('opd', 'from')}>
            <DatePicker
              value={from}
              onChange={(value) => {
                setFrom(value);
                setBody(null);
              }}
            />
          </Field>
        )}
        {type === 'sick_leave' && (
          <Field label={`To${days ? ` (${days} day${days === 1 ? '' : 's'})` : ''}`}>
            <DatePicker
              value={to}
              min={from}
              onChange={(value) => {
                setTo(value);
                setBody(null);
              }}
            />
          </Field>
        )}
        <Field label={tr('opd', 'remarks_or_restrictions')} className="sm:col-span-2 lg:col-span-3">
          <Input
            value={remarks}
            onChange={(e) => {
              setRemarks(e.target.value);
              setBody(null);
            }}
            maxLength={400}
          />
        </Field>
        {type === 'sick_leave' && (
          <Field label={tr('opd', 'seha_reference')} hint={tr('opd', 'needed_in_saudi_arabia_register_the_leave_in')} className="sm:col-span-2 lg:col-span-3">
            <Input value={externalRef} onChange={(e) => setExternalRef(e.target.value)} maxLength={60} />
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('opd', 'certificate_text')} icon={FileText} columns={1}>
        <Field label={tr('opd', 'text_printed_on_the_certificate')}>
          <Textarea rows={5} value={text} onChange={(e) => setBody(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('opd', 'issue_and_print')} loading={issue.isPending} incomplete={text.trim().length < 10} cancelTo={returnTo} />
    </FormScreen>
  );
}
